<template>
    <ValidationObserver ref="validation">
        <label for="parcelType">{{ $t('Type') }}</label>
        <fieldset id="parcelType">
            <div class="form-row">
                <div class="form-group col-md-6">
                    <div v-for="parcel in parcels" class="input-group">
                        <div class="input-group-prepend">
                            <div class="input-group-text">
                                <input :id="'parcel' + parcel.id" v-model="parcelId" :aria-label="$te(parcel.name) ? $t(parcel.name) : parcel.name" type="radio" :value="parcel.id" @change="setParcel">
                            </div>
                        </div>
                        <div class="form-control">
                            <label class="form-check-label" :for="'parcel' + parcel.id">{{ $te(parcel.name) ? $t(parcel.name) : parcel.name }}</label>
                        </div>
                    </div>
                    <div class="input-group">
                        <div class="input-group-prepend">
                            <div class="input-group-text">
                                <input id="typeCustom" v-model="parcelId" :aria-label="$t('Custom…')" type="radio" :value="null">
                            </div>
                        </div>
                        <div class="form-control">
                            <label class="form-check-label" for="typeCustom">{{ $t('Custom…') }}</label>
                        </div>
                    </div>
                </div>
            </div>
        </fieldset>
        <label for="parcelWeight">{{ $t('Weight (kg)') }}</label>
        <fieldset id="parcelWeight">
            <div class="form-row">
                <div class="form-group col-md-6">
                    <label class="sr-only" for="weight">{{ $t('Weight') }}</label>
                    <ValidationProvider v-slot="{ errors, classes, ariaMsg, ariaInput }" :rules="`required|min_value:0.1|max_value:${maxWeight}`" vid="weight">
                        <div class="input-group">
                            <div class="input-group-prepend">
                                <div class="input-group-text"><i class="fa fa-weight-hanging"></i></div>
                            </div>
                            <input id="weight" v-model="weight" v-bind="ariaInput" :class="classes" :disabled="disabled" :placeholder="$t('Weight')"
                                   class="form-control" :max="maxWeight" min="0.1" step="0.1" type="number" @change="updateWeight"/>
                            <span v-bind="ariaMsg" class="invalid-feedback">{{ errors[0] }}</span>
                        </div>
                    </ValidationProvider>
                </div>
            </div>
        </fieldset>
        <label for="parcel">{{ $t('Size (cm)') }}</label>
        <fieldset id="parcel">
            <div class="form-row">
                <div class="form-group col-md-4">
                    <label class="sr-only" for="height">{{ $t('Height') }}</label>
                    <ValidationProvider v-slot="{ errors, classes, ariaMsg, ariaInput }" :rules="`required|min_value:1|max_value:${maxHeight}`" vid="height">
                        <div class="input-group">
                            <div class="input-group-prepend">
                                <div class="input-group-text"><i class="fa fa-arrows-alt-v"></i></div>
                            </div>
                            <input id="height" v-model="height" v-bind="ariaInput" :class="classes" :disabled="disabled" :placeholder="$t('Height')"
                                   class="form-control" :max="maxHeight" min="1" type="number" @change="updateHeight"/>
                            <span v-bind="ariaMsg" class="invalid-feedback">{{ errors[0] }}</span>
                        </div>
                    </ValidationProvider>
                </div>
                <div class="form-group col-md-4">
                    <label class="sr-only" for="width">{{ $t('Width') }}</label>
                    <ValidationProvider v-slot="{ errors, classes, ariaMsg, ariaInput }" :rules="`required|min_value:1|max_value:${maxWidth}`" vid="width">
                        <div class="input-group">
                            <div class="input-group-prepend">
                                <div class="input-group-text"><i class="fa fa-arrows-alt-h"></i></div>
                            </div>
                            <input id="width" v-model="width" v-bind="ariaInput" :class="classes" :disabled="disabled" :placeholder="$t('Width')"
                                   class="form-control" :max="maxWidth" min="1" type="number" @change="updateWidth"/>
                            <span v-bind="ariaMsg" class="invalid-feedback">{{ errors[0] }}</span>
                        </div>
                    </ValidationProvider>
                </div>
                <div class="form-group col-md-4">
                    <label class="sr-only" for="length">{{ $t('Length') }}</label>
                    <ValidationProvider v-slot="{ errors, classes, ariaMsg, ariaInput }" :rules="`required|min_value:0.5|max_value:${maxLength}`" vid="length">
                        <div class="input-group">
                            <div class="input-group-prepend">
                                <div class="input-group-text"><i class="fa fa-expand-alt"></i></div>
                            </div>
                            <input id="length" v-model="length" v-bind="ariaInput" :class="classes" :disabled="disabled" :placeholder="$t('Length')"
                                   class="form-control" :max="maxLength" min="1" type="number" @change="updateLength"/>
                            <span v-bind="ariaMsg" class="invalid-feedback">{{ errors[0] }}</span>
                        </div>
                    </ValidationProvider>
                </div>
            </div>
        </fieldset>
        <label for="parcelContent">{{ $t('Content') }}</label>
        <fieldset id="parcelContent">
            <div class="form-row">
                <div class="form-group col-md-6">
                    <label class="sr-only" for="weight">{{ $t('Category') }}</label>
                    <div class="input-group">
                        <div class="input-group-prepend">
                            <div class="input-group-text"><i class="fa fa-folder"></i></div>
                        </div>
                        <select2 id="category" v-model="categoryId" :placeholder="$t('Category')"
                                 :options="categories" class="form-control" :disabled="disabled">
                        </select2>
                    </div>
                </div>
                <div class="form-group col-md-6">
                    <label class="sr-only" for="weight">{{ $t('Subcategory') }}</label>
                    <div class="input-group">
                        <div class="input-group-prepend">
                            <div class="input-group-text"><i class="fa fa-sitemap"></i></div>
                        </div>
                        <select2 id="subcategory" v-model="subcategoryId" :placeholder="$t('Subcategory')"
                                 :options="subcategories" class="form-control" :disabled="disabled || !subcategories.length" @change="updateSubcategory">
                        </select2>
                    </div>
                </div>
                <div class="form-group col-md-12">
                    <label class="sr-only" for="weight">{{ $t('Description') }}</label>
                    <ValidationProvider v-slot="{ errors, classes, ariaMsg, ariaInput }" rules="required" vid="description">
                        <div class="input-group">
                            <div class="input-group-prepend">
                                <div class="input-group-text"><i class="fa fa-pen"></i></div>
                            </div>
                            <textarea id="description" v-model="description" v-bind="ariaInput" :class="classes" :disabled="disabled" :placeholder="$t('Description')"
                                   class="form-control" @change="updateDescription"/>
                            <span v-bind="ariaMsg" class="invalid-feedback">{{ errors[0] }}</span>
                        </div>
                    </ValidationProvider>
                </div>
            </div>
        </fieldset>
    </ValidationObserver>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import Select2 from "../Select2";

export default {
    name: "ParcelForm",
    components: {
        ValidationProvider,
        ValidationObserver,
        Select2,
    },
    props: {
        maxWeight: {type: Number},
        maxHeight: {type: Number},
        maxWidth: {type: Number},
        maxLength: {type: Number},
    },
    data() {
        return {
            parcelId: undefined,
            weight: this.$store.state.order.parcel.weight,
            height: this.$store.state.order.parcel.height,
            width: this.$store.state.order.parcel.width,
            length: this.$store.state.order.parcel.length,
            categoryId: this.$store.state.order.parcel.subcategory?.categoryId,
            subcategoryId: this.$store.state.order.parcel.subcategory?.id,
            description: this.$store.state.order.parcel.description,
        }
    },
    computed: {
        errors() {
            return this.$refs.validation ? this.$refs.validation.$data.errors : undefined;
        },
        parcels() {
            return this.$store.state.order.parcel.parcels;
        },
        categories() {
            return this.$store.state.order.parcel.categories;
        },
        subcategories() {
            return this.category?.subcategories ?? [];
        },
        parcel() {
            return this.parcels.filter(parcel => parcel.id === this.parcelId).shift();
        },
        category() {
            return this.categories.find(category => category.id === this.categoryId);
        },
        subcategory() {
            return this.subcategories.find(subcategory => subcategory.id === this.subcategoryId);
        },
        disabled() {
            return this.parcelId === undefined;
        },
    },
    methods: {
        setParcel() {
            this.weight = this.parcel.weight;
            this.height = this.parcel.height;
            this.width = this.parcel.width;
            this.length = this.parcel.length;
            this.categoryId = this.parcel.subcategory.category_id;
            this.description = this.parcel.name;
            setTimeout(() => {
                this.subcategoryId = this.parcel.subcategory.id;
                this.$refs.validation.validate().then(() => {
                    this.updateWeight();
                    this.updateHeight();
                    this.updateWidth();
                    this.updateLength();
                    this.updateSubcategory();
                    this.updateDescription();
                });
            }, 100);

        },
        updateWeight() {
            this.$store.commit('order/parcel/setWeight', this.errors && this.errors.weight.length ? undefined : this.weight);
        },
        updateHeight() {
            this.$store.commit('order/parcel/setHeight', this.errors && this.errors.height.length ? undefined : this.height);
        },
        updateWidth() {
            this.$store.commit('order/parcel/setWidth', this.errors && this.errors.width.length ? undefined : this.width);
        },
        updateLength() {
            this.$store.commit('order/parcel/setLength', this.errors && this.errors.length.length ? undefined : this.length);
        },
        updateSubcategory() {
            this.$store.commit('order/parcel/setSubcategory', this.errors && this.errors.length.subcategory ? undefined : this.subcategory);
        },
        updateDescription() {
            this.$store.commit('order/parcel/setDescription', this.errors && this.errors.length.description ? undefined : this.description);
        },
    },
    mounted() {
        this.$store.dispatch('order/parcel/loadParcels');
        this.$store.dispatch('order/parcel/loadCategories');
    }
}
</script>
